import "./FacilityClaimInbox.scss";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GridLoader, ScaleLoader } from "react-spinners";
import newRequest from "../../../utils/newRequest";
import { getCssValue, initCap } from "../../../utils/genericFunctions";
import {
    TablePagination,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
} from "@mui/material";
import { IoIosRefresh } from "react-icons/io";
import { CiImageOn } from "react-icons/ci";
import { MdClose, MdEmail, MdOutlineNumbers, MdPhone } from "react-icons/md";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { showToast } from '../../../toastService';
import CircularButton, { CustomLoadingButton } from "../../../components/ui/CustomButton";
import OnScreenReports from "../../../components/onScreenReports/OnScreenReports";
import { IoDocumentTextSharp } from "react-icons/io5";


const FacilityClaimInbox = () => {
    const { t } = useTranslation();
    const socket = useSelector((state) => state.socket.socket);
    const { currentTheme } = useSelector((state) => state.theme);
    const { currentUser } = useSelector((state) => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const navigate = useNavigate();
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentClaimPage, setCurrentClaimPage] = useState(0);
    const [searchTermLocation, setSearchTermLocation] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [allClaims, setAllClaims] = useState([]);
    const [claims, setClaims] = useState([]);
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [hasNewClaim, setHasNewClaim] = useState(false);
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [priorities, setPriorities] = useState([]);
    const [reportGroupArray, setReportKeysArray] = useState(["all-claims"]);
    const [reportsDialogOpen, setReportsDialogOpen] = useState(false);

    // claims
    const handleClaimPageChange = (event, newPage) => {
        setCurrentClaimPage(newPage);
    };

    const handleClaimRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentClaimPage(0);
    };


    const filteredClaims = claims?.filter(
        (row) =>
            row?.cm_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row?.cm_remarks?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row?.cm_facility_ref_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row?.cm_member_ent_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row?.cm_search_params?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const paginatedClaims = filteredClaims?.slice(
        currentClaimPage * rowsPerPage,
        currentClaimPage * rowsPerPage + rowsPerPage
    );



    const fetchPriorities = async () => {
        try {
            const res = await newRequest.post("/sysCode/findAll", { sys_type: "Priority Level" });
            const options = [{ _id: "All", sys_code: "All", sys_name: "All", sys_desc: "View all visits" }, ...res.data];
            setPriorities(options);
            setSelectedPriority(options[0]);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchInboxClaims = async () => {
        try {
            setIsProcessing(true);
            const res = await newRequest.post("/meds/inboxClaims", {
                claim_statuses: ["Awaiting Approval", "Response Received", "Awaiting Payment", "Partially Paid", "Fully Paid", "Rejected", "Fradulent", "Closed"],
                facilities_ent_codes: [],
                result_limit: 50,
            });
            setAllClaims(res.data);
            setClaims(res.data);
            setIsProcessing(false);
        } catch (error) {
            console.log(error);
        }
    };

    const handleSearchClaims = async () => {
        try {
            setIsLoading(true);
            if (searchTerm) {
                setCurrentClaimPage(0);
                const res = await newRequest.post("/meds/search",
                    {
                        cm_facility_ent_code: "",
                        search_input: searchTerm
                    });
                setClaims(res.data);
            } else {
                fetchInboxClaims();
            }
        } catch (error) {
            console.error("Error searching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchInboxClaims();
        fetchPriorities();
        // Listen for new claim events
        socket.on("newClaim", (claim) => {
            showToast(`New: ${claim?.cm_no}`, "success");
            setClaims((prevClaims) => [...prevClaims, claim]);
            setHasNewClaim(true); // Notify the user of a new feed
        });

        return () => {
            socket.off("newClaim");
        };
    }, []);

    return (
        <motion.div
            className="facility-claim-inbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
        >
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader className="scaler-loader" />
                </div>
            ) : (
                <div className="inbox-main-wrapper">
                    <div className="navbar">
                        <div className="navbar-left">
                            <h1 className="main-title">{selectedPriority?.sys_name}</h1>
                        </div>
                        <div className="navbar-right">
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="left"
                                endIcon={<IoDocumentTextSharp />}
                                onClick={() => setReportsDialogOpen(true)}>
                                {t('reports')}
                            </CustomLoadingButton>
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="right"
                                endIcon={<IoIosRefresh />} onClick={fetchInboxClaims}>
                                {t('refresh')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                    <div className="content-summary-wrapper">
                        <div className="content-wrapper">
                            <div className="table-content">
                                <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }} className="table-container">
                                    <Table sx={{ width: "max-content", border: 'none' }}>
                                        <TableHead className="tablehead">
                                            <TableRow className="tablerow">
                                                <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                    #
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "80px" }}>
                                                    <span>{t('no').toUpperCase()}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                    <div className="search-input-wrapper" style={{ width: "100%" }}>
                                                        <FiSearch className="search-input-icon" />
                                                        <input
                                                            className="search-input"
                                                            type="text"
                                                            placeholder={`${t('search')} ${t('claim')}`}
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleSearchClaims();
                                                            }}
                                                        />
                                                        {searchTerm.length > 0 && (
                                                            <MdClose
                                                                onClick={async (e) => {
                                                                    try {
                                                                        setSearchTerm("");
                                                                        await fetchInboxClaims();
                                                                    } catch (error) {
                                                                        console.log(error);
                                                                    }
                                                                }}
                                                                className="search-input-icon"
                                                            />
                                                        )}
                                                        {searchTerm.length === 0 && (
                                                            <span className="search-input-icon" />
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                    <span></span>
                                                </TableCell>
                                                <TableCell className="table-cell center" style={{ maxWidth: "150px" }}>
                                                    <span>{t('status').toUpperCase()}</span>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody className="tablebody">
                                            {paginatedClaims?.map((row, index) => (
                                                <TableRow
                                                    key={row._id}
                                                    className={`${selectedClaim?._id === row._id ? "tablerow current" : "tablerow"}`}
                                                    onClick={() => {
                                                        try {
                                                            setSelectedClaim(row);
                                                            navigate(`/cfm/${row._id}`);
                                                        } catch (error) {
                                                            console.log(error);
                                                        }
                                                    }}
                                                >
                                                    <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                        {currentClaimPage * rowsPerPage + index + 1}
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "80px" }}>
                                                        <span className="sub-title">{row?.cm_no}</span>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                                                            <div className='current-user-avatar'>
                                                                <div className="user-wrapper">
                                                                    {row?.facility_details_xx?.ent_photo_url ? <img src={row?.facility_details_xx?.ent_photo_url} alt="" className='avatar' />
                                                                        :
                                                                        <CiImageOn style={{
                                                                            display: "flex",
                                                                            width: "30px",
                                                                            height: "30px",
                                                                            color: "#ccc",
                                                                            borderRadius: "50%",
                                                                            objectFit: "cover"
                                                                        }} />
                                                                    }
                                                                    <div className="user-org-view">
                                                                        <span className="sub-title nowrap">{row?.facility_details_xx?.ent_name}</span>
                                                                        <span style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
                                                                            <MdOutlineNumbers className="icon" />
                                                                            <h1 className="sub-title xrsmall">{row?.facility_details_xx?.ent_code}</h1>
                                                                            <MdEmail className="icon" />
                                                                            <h1 className="sub-title xrsmall">{row?.facility_details_xx?.ent_email || "Unspecified"}</h1>
                                                                            <MdPhone className="icon" />
                                                                            <h1 className="sub-title xrsmall">{row?.facility_details_xx?.ent_phone_no || "Unspecified"}</h1>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                        <div style={{ display: "flex", flexDirection: "row", width: "100%", gap: "5px" }}>
                                                            <span className={`status ${getCssValue(row?.cm_priority_level)}`} style={{ width: "2px" }} />
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                                <span className="sub-title">{row?.cm_priority_level}</span>
                                                                <span className="sub-title xrsmall soft">{row?.time_ago_xx}</span>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="table-cell center" style={{ maxWidth: "150px" }}>
                                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", width: "100%" }}>
                                                            <span className={`sub-title xrsmall status ${getCssValue(row?.cm_status)}`}>{row?.cm_status}</span>
                                                            <span className="sub-title xrsmall soft">{`${initCap(row?.facility_details_xx?.ent_county_xx)} county`}</span>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {claims?.length > rowsPerPage &&
                                        <TablePagination
                                            rowsPerPageOptions={[10]}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={handleClaimRowsPerPage}
                                            component="div"
                                            count={claims?.length}
                                            rows={claims}
                                            page={currentClaimPage}
                                            onPageChange={handleClaimPageChange}
                                        />}
                                </TableContainer>
                            </div>
                        </div>
                        <div className="summary-wrapper">
                            <div className="summary-content">
                                <div className="options-list">
                                    {priorities?.map((row) => (
                                        <span
                                            key={row._id}
                                            className={`${selectedPriority?._id === row._id
                                                ? "single-option active"
                                                : "single-option"
                                                }`}
                                            onClick={async () => {
                                                try {
                                                    if (row?.sys_code === "All") {
                                                        await fetchInboxClaims();
                                                        setSelectedPriority(row);
                                                    } else {
                                                        setSelectedPriority(row);
                                                        const filteredClaims = allClaims.filter(
                                                            (claim) => claim?.cm_priority_level === row?.sys_code
                                                        );
                                                        setClaims(filteredClaims);
                                                    }
                                                } catch (error) {
                                                    console.log(error);
                                                }
                                            }}
                                        >
                                            <h1 className="sub-title">{row?.sys_name}</h1>
                                            <h1 className="sub-title soft">{row?.sys_desc}</h1>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {reportsDialogOpen && (
                <motion.div
                    className="overlay-dialog reports"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">{t('reports')}</h1>
                            <CircularButton currentTheme={currentTheme} onClick={() => setReportsDialogOpen(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <OnScreenReports
                                reportGroupArray={reportGroupArray}
                                documentData={{
                                    docNo: "1",
                                    docId: "1",
                                    docType: "AllClaim",
                                    format: "xlsx",
                                    entity_code: "",
                                    date_fm: "",
                                    date_to: "",
                                    status: "",
                                }} />
                        </div>
                    </div>
                </motion.div>
            )}
            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <GridLoader
                            color="#F44336"
                            style={{
                                height: "50px",
                                width: "50px",
                            }}
                        />
                    </div>
                </motion.div>
            )}
        </motion.div>
    )
}

export default FacilityClaimInbox